/**
 * Slug → user-facing copy map for `<pk-auth-error-banner>`.
 *
 * The `message` field on `{ error, message, correlationId }` API responses is
 * developer-facing — never user-facing. This map is the canonical translation
 * layer from API slug to the wording we show end users.
 *
 * Add a new entry whenever the API adds a new auth slug. The component falls
 * back to the `unknown` entry (with `console.warn`) for any slug not listed
 * here, so the UI stays defensive against API drift.
 */

export type PkAuthTone = 'error' | 'warn' | 'info';

export interface PkAuthCtaSpec {
    /** Button / link label. */
    readonly label: string;
    /**
     * Action identifier emitted on `pk-auth-cta-click` so the consumer can
     * decide what to navigate to / what handler to run.
     */
    readonly action: string;
    /** Optional href for screen-reader / browser default link behavior. */
    readonly href?: string;
}

export interface PkAuthErrorEntry {
    readonly headline: string;
    readonly body: string;
    readonly tone:   PkAuthTone;
    /** Primary CTA; `null` if the slug has no recovery action. */
    readonly primaryCta:   PkAuthCtaSpec | null;
    /** Optional secondary CTA (e.g. "Forgot password" alongside "Sign in"). */
    readonly secondaryCta: PkAuthCtaSpec | null;
}

const SIGN_IN_CTA: PkAuthCtaSpec = {
    label: 'Sign in',
    action: 'sign-in',
    href: '/portal/login',
};

const REQUEST_NEW_LINK_CTA: PkAuthCtaSpec = {
    label: 'Request a new link',
    action: 'request-new-link',
};

const FORGOT_PASSWORD_CTA: PkAuthCtaSpec = {
    label: 'Forgot password',
    action: 'forgot-password',
};

const TOTP_REPROMPT_CTA: PkAuthCtaSpec = {
    label: 'Enter code',
    action: 'totp-reprompt',
};

/**
 * Slug → copy mapping. Keys are the literal `error` slugs returned by the
 * API. Values are presentational entries the component renders verbatim.
 *
 * The `unknown` key is the fallback rendered for any slug not listed here.
 */
export const PK_AUTH_ERROR_MAP: Readonly<Record<string, PkAuthErrorEntry>> = {
    'invalid-token': {
        headline: 'Link not recognised',
        body: 'This link is invalid or no longer active.',
        tone: 'warn',
        primaryCta: SIGN_IN_CTA,
        secondaryCta: null,
    },
    'refresh-token-revoked': {
        headline: 'Session ended',
        body: 'Your session was ended. Sign in again to continue.',
        tone: 'warn',
        primaryCta: SIGN_IN_CTA,
        secondaryCta: null,
    },
    'refresh-token-replayed': {
        headline: 'Security check',
        body: 'We detected unusual activity and invalidated your sessions. Please sign in again on every device you use this account from.',
        tone: 'error',
        primaryCta: SIGN_IN_CTA,
        secondaryCta: null,
    },
    'token-already-used': {
        headline: 'Link already used',
        body: 'This verification link has already been used.',
        tone: 'info',
        primaryCta: REQUEST_NEW_LINK_CTA,
        secondaryCta: null,
    },
    'token-expired': {
        headline: 'Link expired',
        body: 'This link has expired. Request a new one to continue.',
        tone: 'info',
        primaryCta: REQUEST_NEW_LINK_CTA,
        secondaryCta: null,
    },
    'password-reset-token-invalid': {
        headline: 'Reset link invalid',
        body: 'This reset link is invalid or already used. Request a new one to continue.',
        tone: 'info',
        primaryCta: REQUEST_NEW_LINK_CTA,
        secondaryCta: null,
    },
    'invite-already-used': {
        headline: 'Invite already used',
        body: 'Someone has already used this invite. Ask the sender to send a new one.',
        tone: 'info',
        primaryCta: null,
        secondaryCta: null,
    },
    'invite-not-found': {
        headline: 'Invite invalid',
        body: 'This invite is no longer valid.',
        tone: 'info',
        primaryCta: null,
        secondaryCta: null,
    },
    'invite-expired': {
        headline: 'Invite invalid',
        body: 'This invite is no longer valid.',
        tone: 'info',
        primaryCta: null,
        secondaryCta: null,
    },
    'invite-email-mismatch': {
        headline: 'Different email',
        body: 'This invite was sent to a different email address. Sign in with that account or request a new invite.',
        tone: 'warn',
        primaryCta: SIGN_IN_CTA,
        secondaryCta: null,
    },
    'email-in-use': {
        headline: 'Email registered',
        body: 'This email is already registered. Sign in instead, or use "Forgot password" if needed.',
        tone: 'info',
        primaryCta: SIGN_IN_CTA,
        secondaryCta: FORGOT_PASSWORD_CTA,
    },
    'weak-password': {
        headline: 'Password too weak',
        body: 'Choose a stronger password — minimum 12 characters with a mix of letters, numbers, and symbols.',
        tone: 'warn',
        primaryCta: null,
        secondaryCta: null,
    },
    'totp-invalid': {
        headline: 'Wrong code',
        body: "That code didn't match. Try again with a fresh code from your authenticator.",
        tone: 'warn',
        primaryCta: null,
        secondaryCta: null,
    },
    'totp-required': {
        headline: 'Verify with authenticator',
        body: 'This action requires re-verification. Enter the current code from your authenticator.',
        tone: 'info',
        primaryCta: TOTP_REPROMPT_CTA,
        secondaryCta: null,
    },
    'forbidden': {
        headline: 'Not allowed',
        body: "You don't have permission for that action. If you think this is a mistake, contact your administrator.",
        tone: 'error',
        primaryCta: null,
        secondaryCta: null,
    },
    'rate-limited': {
        headline: 'Too many attempts',
        body: "You've tried too many times. Try again in {retry-after-seconds} seconds.",
        tone: 'warn',
        primaryCta: null,
        secondaryCta: null,
    },
    'missing-bearer': {
        headline: 'Sign-in needed',
        body: 'Sign in to continue.',
        tone: 'info',
        primaryCta: SIGN_IN_CTA,
        secondaryCta: null,
    },
    'unknown': {
        headline: 'Something went wrong',
        body: 'An unexpected error occurred. Reference {correlation-id} if you need to report this.',
        tone: 'error',
        primaryCta: null,
        secondaryCta: null,
    },
};

/** Returns the entry for `slug`, or the `unknown` fallback if not mapped. */
export function lookupAuthEntry(slug: string | null | undefined): {
    entry: PkAuthErrorEntry;
    isFallback: boolean;
} {
    const key = (slug ?? '').trim();
    const hit = key ? PK_AUTH_ERROR_MAP[key] : undefined;
    if (hit) return { entry: hit, isFallback: false };
    return { entry: PK_AUTH_ERROR_MAP['unknown']!, isFallback: true };
}
